import React from "react";
import { getLeagueStatus, previewStatus } from "./leagueStatus";
import useMinuteTick from "./useMinuteTick";

// The league status line — "Starts in 11h", "Ends in 3d", "Ended Aug 22",
// "Pending approval" — for every surface in League Play.
//
// Client Rev 3: "I looked through my notes for the Status line and View all
// labels throughout league play (private, public, cash games). Hopefully this is
// a token-level change within league play to keep us from drifting… The status
// system we locked was:
//
//     • Starts in 11h  → #006565
//     • Ends in 3d     → #35B23C
//     • Ended Aug 22   → secondary gray
//     • View all       → #004C50"
//
// It HAD drifted, and the reason it drifted is that six screens each wrote their
// own version of the same ternary. The public landing and the league shell both
// carried
//
//     status?.tone === "live" ? "text-[#22A36B]" : "text-[#0F4146]"
//
// — two colours that are in none of the four tokens — while the league row on
// League Play (previewStatus) already had all four correct. So one screen was
// right and the rest were each wrong in their own way.
//
// The fix the client asked for is the token-level one: there is now ONE component
// and it reads its colours from previewStatus, which stays the single definition.
// A screen cannot pick its own status colour any more, because it no longer says
// what colour to use — it just renders this.
//
// `alignToHandle` is the other half of Rev 3 point 2: "the status line is
// floating between the two lines in the organizer identity block. The status line
// should be aligned with the username line in the identity block."
//
// It floated because the status was a single line sitting in a flex row with
// `items-center` beside a TWO-line block (CREATED BY over the handle), so it
// centred against both lines at once and landed in the gap. Rather than nudge it
// down with a hardcoded margin — which would silently break the next time the
// eyebrow's size changes — the status grows a second line of its own: an
// invisible copy of the eyebrow, with the eyebrow's exact classes. Both columns
// are then the same shape, so `items-center` puts line two next to line two by
// construction. Nothing to keep in sync.
const LeagueStatusLine = ({ game, alignToHandle = false, className = "" }) => {
  const status = getLeagueStatus(game);
  const chip = previewStatus(status, game);

  // Keeps "Starts in 59m" honest while the page sits open.
  useMinuteTick(!!status?.countdownValue);

  if (!chip?.text) return null;

  const body = (
    <span
      className={`font-semibold ${
        chip.pill ? "inline-flex rounded-full px-2.5 py-1 text-xs" : "text-sm"
      } ${chip.cls}`}
    >
      {chip.text}
    </span>
  );

  if (!alignToHandle) {
    return <span className={`shrink-0 ${className}`}>{body}</span>;
  }

  return (
    <span className={`shrink-0 text-right ${className}`}>
      {/* Not rendered to a screen reader, and not a layout constant either — it
          is the eyebrow's own classes, so it is the eyebrow's own height.
          See CreatedByBlock, which this mirrors. */}
      <span
        aria-hidden="true"
        className="block text-[10px] font-semibold uppercase tracking-wider invisible"
      >
        Created by
      </span>
      <span className="block">{body}</span>
    </span>
  );
};

export default LeagueStatusLine;
